/**
 * MetricsCards Component
 * Display key metrics cards.
 *
 * The thumbs-up/down cards were removed — those fields had no write path
 * anywhere and permanently displayed 0. Replaced with bot-vs-human handling
 * and resolution status, both computed from real Conversation fields.
 */

import React from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import { Chat, Message, SmartToy, CheckCircle } from '@mui/icons-material';
import { ChatMetrics } from '@/hooks/useAnalytics';

interface MetricsCardsProps {
  metrics: ChatMetrics | null;
  loading: boolean;
}

const MetricsCards: React.FC<MetricsCardsProps> = ({ metrics, loading }) => {
  const total = metrics?.totalConversations || 0;
  const pct = (n: number) => (total > 0 ? ` (${Math.round((n / total) * 100)}%)` : '');

  const botHandled = metrics?.botHandledConversations || 0;
  const resolved = metrics?.resolvedConversations || 0;

  const cards = [
    {
      title: 'Total Conversations',
      value: total.toLocaleString(),
      icon: <Chat color="primary" />,
      color: '#0ea5e9',
    },
    {
      title: 'Total Messages',
      value: (metrics?.totalMessages || 0).toLocaleString(),
      icon: <Message color="primary" />,
      color: '#0ea5e9',
    },
    {
      title: 'Bot Handled',
      value: `${botHandled.toLocaleString()}${pct(botHandled)}`,
      icon: <SmartToy color="success" />,
      color: '#10b981',
    },
    {
      title: 'Resolved',
      value: `${resolved.toLocaleString()}${pct(resolved)}`,
      icon: <CheckCircle color="success" />,
      color: '#10b981',
    },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {card.title}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color={card.color}>
                    {loading ? '...' : card.value}
                  </Typography>
                </Box>
                <Box sx={{ opacity: 0.3 }}>{card.icon}</Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default MetricsCards;
